/* ==========================================================================
   ns-addon-analytics.js: add-on and offer analytics
   ==========================================================================

   Publishes ns_addon_view, ns_addon_click and ns_addon_add through
   Shopify.analytics.publish (payload: addon-analytics/CONTRACT.md section 2).
   The Liquid marks every widget with data-ns-surface / data-ns-item /
   data-ns-slot / data-ns-anchor, so nothing here knows a section's markup:

   - view:  IntersectionObserver, once per page load per (surface, item).
   - click: delegated. A ticked add-on checkbox, a selected pack or bundle
            radio, or a cross-sell Add / quick-add button ([data-ns-click]).
   - add:   PDP add-ons on "ns:addons:added" (dispatched by <bl-product-addons>
            in custom.js once its POST resolves OK); packs, bundles and
            cross-sell on "variant:added", read back from the _ns_* fields of
            the form that submitted.

   Nothing here fetches /cart*, blocks a submit or throws.
   ========================================================================== */
(function () {
  'use strict';

  var SURFACE_SELECTOR = '[data-ns-surface]';
  var DRAWER_ID = 'mini-cart';
  var OFFER_SURFACES = { price_pack: 'pdp_price_pack', variant_pack: 'pdp_variant_pack', bundle: 'pdp_bundle' };

  var viewed = {};
  var watched = typeof WeakSet === 'function' ? new WeakSet() : null;
  var observer = null;

  function publish(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function' && window.nsAnalytics && window.nsAnalytics.store) {
        data.v = 1;
        data.store = window.nsAnalytics.store;
        Shopify.analytics.publish(name, data);
      }
    } catch (error) {
      // Analytics never gets to break the storefront
    }
  }

  /** The cart quick-add sheet lifts a product page's markup; its widgets are not PDP surfaces. */
  function isTracked(element) {
    return !element.closest('bl-cart-quick-add');
  }

  function pageOf(element) {
    return element.closest('#' + DRAWER_ID) ? 'cart_drawer' : 'product';
  }

  /**
   * Cross-sell cards of products already in the cart are removed from the list, so the slot Liquid rendered
   * can be off. The live position among the cards is the slot the shopper saw.
   */
  function slotOf(element) {
    var parent = element.parentElement;

    if (parent && parent.hasAttribute('data-bl-cart-rec-list')) {
      return Array.prototype.indexOf.call(parent.children, element) + 1;
    }

    return parseInt(element.getAttribute('data-ns-slot'), 10) || 1;
  }

  function payload(element, extra) {
    var data = {
      surface: element.getAttribute('data-ns-surface'),
      anchor: element.getAttribute('data-ns-anchor') || '',
      item: element.getAttribute('data-ns-item') || '',
      slot: slotOf(element),
      page: pageOf(element)
    };

    for (var key in extra) data[key] = extra[key];

    return data;
  }

  /* ---------------------------------------------------------------- views */

  function isShown(element) {
    var drawer = element.closest('#' + DRAWER_ID);

    if (drawer && !drawer.hasAttribute('open')) return false;

    if (typeof element.checkVisibility === 'function' && !element.checkVisibility({ visibilityProperty: true })) return false;

    return true;
  }

  function viewKey(element) {
    return element.getAttribute('data-ns-surface') + '|' + element.getAttribute('data-ns-item');
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      try {
        var element = entry.target;

        // At least half of the card, or half of the screen for a card taller than the screen
        var rootHeight = (entry.rootBounds && entry.rootBounds.height) || window.innerHeight;
        var visible = entry.isIntersecting && (entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= rootHeight * 0.5);

        if (!visible || !isShown(element)) return;

        var key = viewKey(element);

        if (!viewed[key]) {
          viewed[key] = true;
          publish('ns_addon_view', payload(element, {}));
        }

        observer.unobserve(element);
      } catch (error) {
        // ignore
      }
    });
  }

  function scan() {
    try {
      if (!observer && typeof IntersectionObserver === 'function') {
        observer = new IntersectionObserver(onIntersect, { threshold: [0, 0.25, 0.5, 0.75, 1] });
      }

      if (!observer) return;

      document.querySelectorAll(SURFACE_SELECTOR).forEach(function (element) {
        if (!isTracked(element) || viewed[viewKey(element)]) return;

        if (!watched || !watched.has(element)) {
          if (watched) watched.add(element);
          observer.observe(element);
        } else if (element.closest('#' + DRAWER_ID)) {
          // A drawer card that was off screen when it was first seen: observing again forces a fresh reading
          // once the drawer is open
          observer.unobserve(element);
          observer.observe(element);
        }
      });
    } catch (error) {
      // ignore
    }
  }

  var scanTimer = null;

  function scheduleScan() {
    if (scanTimer) return;

    scanTimer = window.setTimeout(function () {
      scanTimer = null;
      scan();
    }, 250);
  }

  /* --------------------------------------------------------------- clicks */

  function onChange(event) {
    try {
      var input = event.target;

      if (!input || !input.matches || !input.matches('input[type="checkbox"], input[type="radio"]') || !input.checked) return;

      var element = input.closest(SURFACE_SELECTOR);

      if (element && isTracked(element)) publish('ns_addon_click', payload(element, {}));
    } catch (error) {
      // ignore
    }
  }

  function onClick(event) {
    try {
      var button = event.target && event.target.closest ? event.target.closest('[data-ns-click]') : null;
      var element = button && button.closest(SURFACE_SELECTOR);

      if (element && isTracked(element)) publish('ns_addon_click', payload(element, {}));
    } catch (error) {
      // ignore
    }
  }

  /**
   * The direct Add form is rendered from a cached recommendations response, so its slot and anchor can be stale.
   * Set them from the live list right before the theme reads the form.
   */
  function onSubmit(event) {
    try {
      var form = event.target;
      var card = form && form.closest ? form.closest('.bl-cart-rec[data-ns-surface]') : null;

      if (!card) return;

      var recommendations = card.closest('bl-cart-recommendations');
      var slot = form.querySelector('input[name="properties[_ns_slot]"]');
      var anchor = form.querySelector('input[name="properties[_ns_anchor]"]');

      if (slot) slot.value = String(slotOf(card));
      if (anchor && recommendations && recommendations.getAttribute('data-anchor-handle')) {
        anchor.value = recommendations.getAttribute('data-anchor-handle');
      }
    } catch (error) {
      // ignore
    }
  }

  /* ----------------------------------------------------------------- adds */

  /** Value of a line item property the form will post: top-level or on any items[n] line. */
  function field(form, key) {
    var found = null;

    Array.prototype.forEach.call(form.elements, function (element) {
      if (found || element.disabled || !element.name) return;

      if (element.name === 'properties[' + key + ']' || new RegExp('^items\\[\\d+\\]\\[properties\\]\\[' + key + '\\]$').test(element.name)) {
        found = element;
      }
    });

    return found;
  }

  function onVariantAdded(event) {
    try {
      var form = event.target;

      if (!form || !form.elements) return;

      var srcInput = field(form, '_ns_src');

      if (srcInput) {
        var src = srcInput.value;

        if (src !== 'cart_xsell' && src !== 'cart_quickadd') return;

        var anchorInput = field(form, '_ns_anchor'), slotInput = field(form, '_ns_slot');
        var card = form.closest('[data-ns-item]');

        publish('ns_addon_add', {
          surface: src,
          anchor: anchorInput ? anchorInput.value : '',
          item: srcInput.getAttribute('data-ns-item') || (card ? card.getAttribute('data-ns-item') : ''),
          slot: slotInput ? parseInt(slotInput.value, 10) || 1 : 1,
          page: 'cart_drawer'
        });

        return;
      }

      var offerInput = field(form, '_ns_offer');

      if (!offerInput || !offerInput.value) return;

      var offer = offerInput.value;
      var surface = OFFER_SURFACES[offer.split(':')[0]];

      if (!surface) return;

      var element = null;

      document.querySelectorAll('[data-ns-surface="' + surface + '"]').forEach(function (candidate) {
        if (!element && candidate.getAttribute('data-ns-item') === offer && isTracked(candidate)) element = candidate;
      });

      publish('ns_addon_add', {
        surface: surface,
        anchor: element ? element.getAttribute('data-ns-anchor') || '' : '',
        item: offer,
        slot: element ? slotOf(element) : 1,
        page: 'product'
      });
    } catch (error) {
      // ignore
    }
  }

  function onAddonsAdded(event) {
    try {
      var items = (event.detail && event.detail.items) || [];

      items.forEach(function (item) {
        var properties = item.properties || {};
        var element = null;

        document.querySelectorAll('[data-ns-surface^="pdp_addon"]').forEach(function (candidate) {
          var input = candidate.querySelector('.bl-addon__input');

          if (!element && input && String(input.value) === String(item.id) && candidate.getAttribute('data-ns-surface') === properties._ns_src) {
            element = candidate;
          }
        });

        publish('ns_addon_add', {
          surface: properties._ns_src,
          anchor: properties._ns_anchor || '',
          item: element ? element.getAttribute('data-ns-item') || '' : '',
          slot: parseInt(properties._ns_slot, 10) || 1,
          page: 'product'
        });
      });
    } catch (error) {
      // ignore
    }
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    document.addEventListener('change', onChange, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('variant:added', onVariantAdded);
    document.addEventListener('ns:addons:added', onAddonsAdded);
    document.addEventListener('cart:refresh', scheduleScan);

    // The drawer's HTML is replaced after every add and product sections re-render on variant change;
    // the drawer opening also needs a fresh look at its cards
    if (typeof MutationObserver === 'function' && document.body) {
      new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    }

    scan();
  }

  try {
    boot();
  } catch (error) {
    // ignore
  }
})();
